// Prozedurales, endloses Asteroidenfeld auf Chunk-Basis.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/field.py`.** Die
// Reihenfolge der Zufallsziehungen in `makeRock` ist Vertrag: der Server zieht
// in exakt derselben Reihenfolge, und beide Seiten pruefen gegen denselben
// Golden Vector (`contracts/determinism.json`, Gate FG-30).
//
// Wer hier eine Ziehung einfuegt, verschiebt alle folgenden und erzeugt ein
// anderes Universum als der Server. Beim Spielen faellt das als „ich schiesse
// ins Leere" auf — nicht als Fehlermeldung.

import { balancing, eraSalt, resource, zoneAtDist, zonesFor } from '../data/balancing';
import type { RockKind } from '../data/types';
import { TAU, jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';

/** Ein Brocken. Form und Erzadern sind reine Darstellung. */
export interface Rock {
  id: string;
  x: number;
  y: number;
  r: number;
  maxHp: number;
  ore: string;
  oreAmount: number;
  xp: number;
  kindId: string;
  rich: boolean;
  zoneIdx: number;
  split: number;
  /** Polygonform: Radiusfaktoren je Ecke. */
  shape: number[];
  /** Erzadern auf der Oberflaeche. */
  pips: Array<{ x: number; y: number; r: number }>;
  rot: number;
  rotv: number;
  vx: number;
  vy: number;
}

export function chunkCoords(x: number, y: number): [number, number] {
  const size = balancing().world.chunk;
  return [Math.floor(x / size), Math.floor(y / size)];
}

export function sectorCoords(cx: number, cy: number): [number, number] {
  const n = balancing().world.sectorChunks;
  return [Math.floor(cx / n), Math.floor(cy / n)];
}

export function sectorOfPosition(x: number, y: number): [number, number] {
  const [cx, cy] = chunkCoords(x, y);
  return sectorCoords(cx, cy);
}

/** Groessenklasse ziehen — kleine Brocken sind haeufiger. */
export function rollKind(rng: () => number): number {
  const roll = rng();
  if (roll < 0.34) return 0;
  if (roll < 0.62) return 1;
  if (roll < 0.84) return 2;
  if (roll < 0.96) return 3;
  return 4;
}

/** Einen Brocken ziehen. **Die Reihenfolge der Ziehungen ist Vertrag.** */
export function makeRock(
  rng: () => number,
  rockId: string,
  x: number,
  y: number,
  kindIdx: number,
  era: number = balancing().eraFirst,
): Rock {
  const data = balancing();
  const zoneIdx = zoneAtDist(Math.hypot(x, y), era);
  const zone = zonesFor(era)[zoneIdx];

  const rich = rng() < data.world.richChance;                     // 1
  const kind: RockKind = rich ? data.richKind : data.rockKinds[kindIdx];
  const r = kind.r[0] + rng() * (kind.r[1] - kind.r[0]);          // 2

  const corners = 7 + Math.floor(rng() * 4);                      // 3
  const shape: number[] = [];
  for (let i = 0; i < corners; i++) shape.push(0.78 + rng() * 0.34);

  let total = 0;
  for (const entry of zone.ores) total += entry.weight;
  let roll = rng() * total;
  let ore = zone.ores[0];
  for (const entry of zone.ores) {
    roll -= entry.weight;
    if (roll <= 0) { ore = entry; break; }
  }

  const pipCount = jsRound((r / 7) * (rich ? 1.6 : 1) * (0.6 + rng()));
  const pips: Rock['pips'] = [];
  for (let i = 0; i < pipCount; i++) {
    const angle = rng() * TAU;
    const dist = Math.sqrt(rng()) * r * 0.62;
    pips.push({ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, r: 1.4 + rng() * 1.8 });
  }

  const gem = resource(ore.res)?.gem ?? false;
  const amount = gem
    ? ore.amount * (0.6 + kind.yield * 0.06) * (1 + zoneIdx * 1.2) * (rich ? 3 : 1)
    : ore.amount * kind.yield * zone.yield * (rich ? 4 : 1);

  const hpBase = kind.hp * zone.hp * (rich ? 2.2 : 1) * (0.85 + rng() * 0.3);

  const vx = (rng() - 0.5) * 10;
  const vy = (rng() - 0.5) * 10;
  const rot = rng() * TAU;
  const rotv = (rng() - 0.5) * 0.5;

  return {
    id: rockId,
    x, y, r,
    maxHp: hpBase,
    ore: ore.res,
    oreAmount: amount,
    xp: kind.xp * zone.xp * (rich ? 3 : 1),
    kindId: kind.id,
    rich,
    zoneIdx,
    split: kind.split,
    shape, pips,
    rot, rotv, vx, vy,
  };
}

/** Wie viele Brocken ein Chunk traegt — erste Ziehung des Chunks. */
export function chunkCapacity(
  rng: () => number,
  cx: number,
  cy: number,
  era: number = balancing().eraFirst,
): number {
  const size = balancing().world.chunk;
  const mid = Math.hypot((cx + 0.5) * size, (cy + 0.5) * size);
  return jsRound((20 + rng() * 14) * zonesFor(era)[zoneAtDist(mid, era)].density);
}

/**
 * Die Weltsaat **einer Ära**. Für Akt I unverändert die eigene.
 *
 * **Wortgleiche Entsprechung von `world/field.py:era_seed`.** Das ist der
 * einzige Punkt, an dem sich die Ären trennen — und er liegt absichtlich
 * *vor* allen Ziehungen, nicht in ihnen. Jeder Saatausdruck der
 * Weltgenerierung behält dadurch seine wörtliche Form; getauscht wird nur die
 * Zahl, die er ohnehin schon las.
 *
 * Der Grund ist die Zusage „Akt I bleibt Byte für Byte": `hash3` über einen
 * Hashwert ist **nicht** die Identität. Hätte man stattdessen die
 * Saatausdrücke selbst geschachtelt — auch mit dem Salz 0 —, sähe jeder
 * bestehende Spieler ein anderes Universum, ohne dass irgendwo ein Fehler
 * auftaucht (Gates FG-20, FG-30).
 */
export function eraSeed(worldSeed: number, era: number = balancing().eraFirst): number {
  const salt = eraSalt(era);
  return salt === null ? worldSeed : hash3(worldSeed, salt, worldSeed);
}

/** Alle Grundbrocken eines Chunks. Reproduzierbar, ohne Zwischenspeicher. */
export function chunkRocks(
  cx: number,
  cy: number,
  worldSeed: number,
  era: number = balancing().eraFirst,
): Rock[] {
  const data = balancing();
  const size = data.world.chunk;
  const rng = rngFrom(hash3(cx, cy, eraSeed(worldSeed, era)));
  const capacity = chunkCapacity(rng, cx, cy, era);
  const rocks: Rock[] = [];
  for (let i = 0; i < capacity; i++) {
    const x = cx * size + rng() * size;
    const y = cy * size + rng() * size;
    const kindIdx = rollKind(rng);
    if (Math.hypot(x, y) < data.world.clearRadius) continue;      // Platz um die Kapsel
    rocks.push(makeRock(rng, `${cx}:${cy}:${i}`, x, y, kindIdx, era));
  }
  return rocks;
}

/**
 * Bruchstuecke eines zerstoerten Brockens — **deterministisch**.
 *
 * Im Vorlaeufer war das Zerspringen bewusst zufaellig; online geht das nicht.
 * Zwei Spieler saehen sonst verschiedene Splitter und der Server einen dritten
 * Satz.
 */
export function fragments(
  parent: Rock,
  count: number,
  worldSeed: number,
  era: number = balancing().eraFirst,
): Rock[] {
  if (count <= 0) return [];
  const data = balancing();
  const [cxRaw, cyRaw, indexRaw] = parent.id.split('#')[0].split(':');
  const cx = Number(cxRaw);
  const cy = Number(cyRaw);
  const index = Number(indexRaw);

  const parentKind = data.rockKinds.findIndex((kind) => kind.id === parent.kindId);
  const kindIdx = parent.rich ? 1 : Math.max(0, parentKind - 1);

  const out: Rock[] = [];
  for (let k = 0; k < count; k++) {
    const rng = rngFrom(hash3(hash3(cx, cy, index), k, eraSeed(worldSeed, era)));
    const angle = (k / count) * TAU + rng() * TAU;
    const fx = parent.x + Math.cos(angle) * parent.r * 0.5;
    const fy = parent.y + Math.sin(angle) * parent.r * 0.5;
    const piece = makeRock(rng, `${parent.id}#${k}`, fx, fy, kindIdx, era);
    // Ein Bruchstueck traegt das Erz seines Ursprungs, nicht das der Zone.
    piece.ore = parent.ore;
    piece.split = 0;
    out.push(piece);
  }
  return out;
}
